import DeleteIcon from "@mui/icons-material/Delete";
import { 
  Box, 
  Checkbox, 
  IconButton, 
  Typography,
  Fade,
  Tooltip,
  alpha
} from "@mui/material";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { useCheckBox, useManagementTextField } from "hooks/forms/ManagementHooks";
import { useQueryClient } from "react-query";
import { updateChecklistItem } from "services/api/checklistItems/checklistItemsApi";
import CustomTrelloInput from "./CustomTrelloInput";

export default function CheckListItem({
  id,
  isChecked,
  content,
  index,
  projectId,
  handleDeleteItem,
}) {
  const checkbox = useCheckBox(isChecked || false);
  const description = useManagementTextField(content || "");

  const queryClient = useQueryClient();

  const handleCheckboxChange = async () => {
    const isChecked = !checkbox.checked;

    // Atualiza o estado local primeiro para feedback imediato
    checkbox.onCustomChange(isChecked);

    // Atualiza o cache local
    queryClient.setQueryData(["checklistByProject", projectId], (oldData) => {
      if (!oldData) return oldData;

      const newChecklistItems = oldData.checklistItems.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            isChecked,
          };
        }

        return item;
      });

      return {
        ...oldData,
        checklistItems: newChecklistItems,
      };
    });

    // Faz a requisição para o backend
    try {
      await updateChecklistItem({
        id,
        content,
        isChecked,
        order: index + 1,
      });
      queryClient.invalidateQueries(["projectActivityFeed", projectId]);
    } catch (error) {
      console.error("Erro ao atualizar item do checklist:", error);
      // Reverte a mudança local em caso de erro
      checkbox.onCustomChange(!isChecked);
      queryClient.setQueryData(["checklistByProject", projectId], (oldData) => {
        if (!oldData) return oldData;

        const revertedChecklistItems = oldData.checklistItems.map((item, i) => {
          if (i === index) {
            return {
              ...item,
              isChecked: !isChecked,
            };
          }

          return item;
        });

        return {
          ...oldData,
          checklistItems: revertedChecklistItems,
        };
      });
    }
  };

  return (
    <Fade in timeout={300}>
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center",
          gap: 0.5,
          py: 0.5,
          px: 1,
          borderRadius: 1,
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
            '& .delete-btn': { opacity: 1 },
          }
        }}
      >
        {/* Checkbox compacto */}
        <Checkbox 
          checked={checkbox.checked} 
          onChange={handleCheckboxChange}
          icon={<RadioButtonUnchecked sx={{ fontSize: 18 }} />}
          checkedIcon={<CheckCircle sx={{ fontSize: 18 }} />}
          size="small"
          sx={{
            p: 0.25,
            color: 'grey.400',
            '&.Mui-checked': {
              color: 'success.main',
            },
          }}
        />

        {/* Campo de texto */}
        <Tooltip title={description.value || ''} placement="top" enterDelay={500} arrow>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <CustomTrelloInput
              checked={checkbox.checked}
              value={description.value}
              onChange={description.onChange}
              sx={{
                '& input': {
                  fontSize: '0.825rem',
                  textDecoration: checkbox.checked ? 'line-through' : 'none',
                  color: checkbox.checked ? 'text.disabled' : 'text.primary',
                  fontWeight: 400,
                  transition: 'all 0.2s',
                }
              }}
            />
          </Box>
        </Tooltip>

        {/* Botão de deletar - aparece no hover */}
        <IconButton 
          className="delete-btn"
          onClick={() => handleDeleteItem(id)}
          size="small"
          sx={{ 
            opacity: 0,
            transition: 'opacity 0.2s',
            p: 0.25,
            color: 'text.disabled',
            '&:hover': {
              color: 'error.main',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
            }
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Fade>
  );
}
